import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Flex,
  Grid,
  GridItem,
  Text,
} from '@everlywell/ui-kit';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';

import Eyebrow from '../Eyebrow';

export type DetailsSectionItem = {
  heading: string;
  description: React.ReactNode;
  symptoms: {
    icon: React.ReactNode;
    heading: string;
    details: React.ReactNode;
  }[];
};

export type DetailsSectionProps = {
  eyebrow?: string;
  items: DetailsSectionItem[];
};

/**
 * Details section for PDP Overview.
 */
function DetailsSection(props: DetailsSectionProps): JSX.Element {
  const { items, eyebrow } = props;

  const handleAccordionButtonClick = (item: DetailsSectionItem) => () => {
    analytics.track({
      event: ANALYTICS.EVENTS.FAQ_CLICK,
      data: {
        question: item.heading,
        component: 'PDPOverviewSection',
      },
    });
  };

  return (
    <Flex flexDirection="column" gap={6}>
      {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
      <Accordion allowMultiple>
        {items.map((item, index) => (
          <AccordionItem
            key={index}
            borderBottomWidth={index === items.length - 1 ? 0 : 1}
          >
            <AccordionButton onClick={handleAccordionButtonClick(item)}>
              <Text size="lg" fontWeight="medium">
                {item.heading}
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel fontWeight="normal" paddingLeft={1}>
              <DetailsSectionPanelContent {...item} />
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Flex>
  );
}

export default DetailsSection;

export type DetailsSectionPanelProps = Omit<DetailsSectionItem, 'heading'>;

/**
 * Renders the content of the accordion panel.
 * @param props
 * @returns
 */
function DetailsSectionPanelContent(props: DetailsSectionPanelProps) {
  const { description, symptoms } = props;

  return (
    <Flex flexDirection="column" gap={4}>
      {description}
      {symptoms.length > 0 ? (
        <Grid columns={[1, 3]} spacing={12} alignEdges>
          {symptoms.map((sympthom) => (
            <GridItem key={sympthom.heading}>
              <Flex flexDirection={['row', 'column']} gap={[3, 4]}>
                {sympthom.icon}
                <Flex flexDirection="column" gap={2}>
                  <Text size="lg" color="tints.black">
                    {sympthom.heading}
                  </Text>
                  {sympthom.details}
                </Flex>
              </Flex>
            </GridItem>
          ))}
        </Grid>
      ) : null}
    </Flex>
  );
}
